import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, TrendingUp, Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";

interface Session {
  id: string;
  interview_type: string;
  status: string;
  overall_score: number;
  started_at: string;
  completed_at: string;
  duration: number;
  question_count: number;
}

const InterviewHistory = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'completed' | 'abandoned'>('all');

  useEffect(() => {
    loadHistory();
  }, [filter]);

  const loadHistory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      let query = supabase
        .from('interview_sessions')
        .select(`
          *,
          interview_answers (count)
        `)
        .eq('user_id', user.id)
        .order('started_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedSessions = data.map((session: any) => ({
        id: session.id,
        interview_type: session.interview_type,
        status: session.status,
        overall_score: session.overall_score || 0,
        started_at: session.started_at,
        completed_at: session.completed_at,
        duration: session.duration || 0,
        question_count: session.interview_answers?.[0]?.count || 0
      }));

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error loading history:', error);
      toast({
        title: "Error",
        description: "Failed to load interview history",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sessionId: string) => {
    if (!confirm("Are you sure you want to delete this session?")) return;

    try {
      const { error } = await supabase
        .from('interview_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Session deleted",
        description: "Interview session has been removed"
      });

      loadHistory();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: "Error",
        description: "Failed to delete session",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string }> = {
      'completed': { variant: 'default', label: 'Completed' },
      'in-progress': { variant: 'secondary', label: 'In Progress' },
      'abandoned': { variant: 'destructive', label: 'Abandoned' }
    };
    const config = variants[status] || variants['in-progress'];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <>
      <Helmet>
        <title>Practice History | AI Interview Prep</title>
      </Helmet>

      
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
          <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold">Practice History</h1>
                <p className="text-muted-foreground mt-2">
                  Review your past interview sessions and track improvement
                </p>
              </div>
              <Button onClick={() => navigate('/app/ai-interview-prep')}>
                Back to AI Interview Prep
              </Button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              {(['all', 'completed', 'abandoned'] as const).map((f) => (
                <Button
                  key={f}
                  variant={filter === f ? 'default' : 'outline'}
                  onClick={() => setFilter(f)}
                  className="capitalize"
                >
                  {f}
                </Button>
              ))}
            </div>

            {/* Sessions List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p>Loading history...</p>
              </div>
            ) : sessions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-muted-foreground mb-4">No interview sessions found</p>
                  <Button onClick={() => navigate('/app/ai-interview-prep/practice/quick')}>
                    Start Your First Practice
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {sessions.map((session) => (
                  <Card key={session.id} className="hover:border-primary/50 transition-all">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <CardTitle className="capitalize">
                            {session.interview_type.replace('-', ' ')} Interview
                          </CardTitle>
                          <CardDescription className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(session.started_at)}
                            </span>
                            {session.duration > 0 && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDuration(session.duration)}
                              </span>
                            )}
                          </CardDescription>
                        </div>
                        {getStatusBadge(session.status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between items-center">
                        <div className="flex gap-6">
                          {session.status === 'completed' && (
                            <>
                              <div>
                                <div className="text-2xl font-bold text-primary">
                                  {session.overall_score.toFixed(1)}/10
                                </div>
                                <div className="text-xs text-muted-foreground">Overall Score</div>
                              </div>
                              <div>
                                <div className="text-2xl font-bold">
                                  {session.question_count}
                                </div>
                                <div className="text-xs text-muted-foreground">Questions</div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {session.status === 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/app/ai-interview-prep/results/${session.id}`)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Results
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(session.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      
    </>
  );
};

export default InterviewHistory;
